import {
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const PASSWORD_MIN_LENGTH = 10;

const normalizeAuthEmail = (email) => (email || '').trim().toLowerCase();

const getTextField = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

export const isPasswordUser = (user) =>
  Array.isArray(user?.providerData)
  && user.providerData.some((provider) => provider.providerId === 'password');

export const getPasswordValidationMessage = (password) => {
  if (!password || password.length < PASSWORD_MIN_LENGTH) {
    return `A jelszó legyen legalább ${PASSWORD_MIN_LENGTH} karakter hosszú.`;
  }

  if (!/[a-záéíóöőúüű]/u.test(password)) {
    return 'A jelszó tartalmazzon kisbetűt.';
  }

  if (!/[A-ZÁÉÍÓÖŐÚÜŰ]/u.test(password)) {
    return 'A jelszó tartalmazzon nagybetűt.';
  }

  if (!/\d/.test(password)) {
    return 'A jelszó tartalmazzon számot.';
  }

  return '';
};

const createAuthError = (code, message) => {
  const error = new Error(message);
  error.code = code;
  error.friendly = true;
  return error;
};

export const upsertUserInFirestore = async (db, user) => {
  const userRef = doc(db, 'users', user.uid);
  const existing = await getDoc(userRef);
  const existingData = existing.exists() ? existing.data() : {};
  const existingBillingName = getTextField(
    existingData.billing_name,
    existingData.billingName,
    existingData.invoice_name,
  );
  const defaultBillingName = (user.displayName || '').trim();

  const baseData = {
    uid: user.uid,
    email: user.email || '',
    displayName: user.displayName || '',
    photoURL: user.photoURL || '',
    providerIds: user.providerData.map((provider) => provider.providerId),
    emailVerified: user.emailVerified === true,
    updatedAt: serverTimestamp(),
    lastLoginAt: serverTimestamp(),
  };

  if (existing.exists()) {
    await updateDoc(userRef, {
      ...baseData,
      ...(defaultBillingName && !existingBillingName ? { billing_name: defaultBillingName } : {}),
    });
    return;
  }

  await setDoc(userRef, {
    ...baseData,
    billing_name: defaultBillingName,
    createdAt: serverTimestamp(),
  });
};

const sendAccountVerificationViaResend = async (cloudFunctions) => {
  if (!cloudFunctions) {
    throw createAuthError('auth/missing-functions', 'A megerősítő e-mail küldés most nem érhető el.');
  }

  const sendAccountVerificationEmail = httpsCallable(cloudFunctions, 'sendAccountVerificationEmail');
  await sendAccountVerificationEmail({});
};

export const registerWithEmailPassword = async ({ auth, cloudFunctions, email, password }) => {
  const normalizedEmail = normalizeAuthEmail(email);

  if (!normalizedEmail) {
    throw createAuthError('auth/missing-email', 'Add meg az e-mail címed.');
  }

  const passwordMessage = getPasswordValidationMessage(password);
  if (passwordMessage) {
    throw createAuthError('auth/weak-password', passwordMessage);
  }

  try {
    const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail);
    if (methods.includes('google.com')) {
      throw createAuthError(
        'auth/google-email-already-in-use',
        'Ez az e-mail cím már Google fiókkal van regisztrálva. Lépj be Google-lel.',
      );
    }

    if (methods.includes('password')) {
      throw createAuthError(
        'auth/email-already-in-use',
        'Ezzel az e-mail címmel már van fiók. Jelentkezz be, vagy kérj jelszó-visszaállítást.',
      );
    }
  } catch (error) {
    if (error?.friendly || error?.code === 'auth/invalid-email') {
      throw error;
    }
  }

  try {
    await createUserWithEmailAndPassword(auth, normalizedEmail, password);
    await sendAccountVerificationViaResend(cloudFunctions);
    await signOut(auth);
  } catch (error) {
    if (error?.code === 'auth/email-already-in-use') {
      let methods = [];
      try {
        methods = await fetchSignInMethodsForEmail(auth, normalizedEmail);
      } catch (lookupError) {
        methods = [];
      }

      if (methods.includes('google.com')) {
        throw createAuthError(
          'auth/google-email-already-in-use',
          'Ez az e-mail cím már Google fiókkal van regisztrálva. Lépj be Google-lel.',
        );
      }
    }

    throw error;
  }
};

export const signInWithVerifiedEmailPassword = async ({ auth, cloudFunctions, db, email, password }) => {
  const normalizedEmail = normalizeAuthEmail(email);

  if (!normalizedEmail || !password) {
    throw createAuthError('auth/missing-credentials', 'Add meg az e-mail címet és a jelszót.');
  }

  const result = await signInWithEmailAndPassword(auth, normalizedEmail, password);
  await result.user.reload();

  if (!result.user.emailVerified) {
    await sendAccountVerificationViaResend(cloudFunctions);
    await signOut(auth);
    throw createAuthError(
      'auth/email-not-verified',
      'A belépéshez előbb erősítsd meg az e-mail címed. Küldtünk egy új megerősítő linket.',
    );
  }

  await upsertUserInFirestore(db, result.user);
  return result.user;
};

export const sendPasswordReset = async ({ cloudFunctions, email }) => {
  const normalizedEmail = normalizeAuthEmail(email);

  if (!normalizedEmail) {
    throw createAuthError('auth/missing-email', 'Add meg az e-mail címed.');
  }

  if (!cloudFunctions) {
    throw createAuthError('auth/missing-functions', 'A jelszó-visszaállító e-mail küldés most nem érhető el.');
  }

  const sendPasswordResetEmailViaResend = httpsCallable(cloudFunctions, 'sendPasswordResetEmail');
  const result = await sendPasswordResetEmailViaResend({ email: normalizedEmail });
  return result?.data || {};
};

export const getFriendlyAuthErrorMessage = (error) => {
  if (error?.friendly && error?.message) {
    return error.message;
  }

  switch (error?.code) {
    case 'auth/invalid-email':
      return 'Az e-mail cím formátuma nem megfelelő.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Nem sikerült belépni. Ellenőrizd az e-mail címet és a jelszót.';
    case 'auth/too-many-requests':
      return 'Túl sok próbálkozás történt. Várj egy kicsit, majd próbáld újra.';
    case 'auth/email-already-in-use':
      return 'Ezzel az e-mail címmel már van fiók. Ha Google-lel regisztráltál, lépj be Google-lel.';
    case 'auth/weak-password':
      return 'A jelszó nem elég erős.';
    case 'auth/operation-not-allowed':
      return 'Ez a belépési mód jelenleg nincs engedélyezve.';
    case 'auth/popup-closed-by-user':
      return 'A Google bejelentkezési ablak bezárult a folyamat előtt.';
    default:
      return 'A művelet most nem sikerült. Próbáld újra később.';
  }
};
